import React, { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { WIKI_SEARCH_INPUT_ID } from './shell';
import type { WikiTagUsage } from '../../types/wiki';

// 列表页的搜索条：一个输入框 + 一排标签候选。**内嵌在页面里，不是浮层**。
//
// 筛选状态走 URL（router.get）而不是组件 state：URL 决定内容，能收藏、能后退
// （routes.rb 里 /todo/:view 那条注释立过同一条规矩）。这也是它不做浮层的连带
// 好处 —— 浮层里「就地筛完」的结果没有地址，关掉就没了。
//
// 与改版前那一版的三处不同，都是 todo 356 那条「标签太长」逼出来的：
//   1. 标签**带页数**且按页数倒序（服务端 WikiTagUsage#ranked），不再是字母序；
//   2. 默认只铺前 TAG_PREVIEW 个，其余收在「全部 N 个」后面 —— 线上是 202 个
//      标签，全铺出来光这一排就有四行；
//   3. 边栏那块一模一样的标签云已经整块删掉了，这里是唯一的一份。
type SearchBarProps = {
  search: string;
  tag: string;
  tags: WikiTagUsage[];
  // 全量有多少个（含只挂一页、进不了候选的那些）。「全部 N 个」那行用它。
  totalTags: number;
  // 命中几条。null = 没在筛选（那时不显示这个数）。
  resultCount: number | null;
};

// 敲完停手 300ms 再发请求。不做「按回车才搜」：这一页的搜索是**扫**，边打边看
// 结果是它的用法；而后端是一条 ILIKE，单人库上毫秒级（spec §9）。
const DEBOUNCE = 300;

// 默认铺几个标签。10 个在 720px 的正文列里大约一行半 —— 再多就开始与「这一页
// 是一列文档」抢视觉重量，而标签是入口，不是内容。
const TAG_PREVIEW = 10;

const SearchBar: React.FC<SearchBarProps> = ({ search, tag, tags, totalTags, resultCount }) => {
  const [draft, setDraft] = useState(search);
  const [showAll, setShowAll] = useState(false);
  // 首次渲染不要发请求：那会把一次普通的进页面变成一次带参数的导航。
  const mounted = useRef(false);

  // 服务端此刻是不是已经在筛选。决定这一跳是推历史还是替换历史。
  const filtering = search !== '' || tag !== '';

  // preserveState —— 不然输入框会在每次响应回来时被重建，光标跳到末尾。
  //
  // replace 只在**已经**在筛选时为真：
  // - 从没筛选 → 筛选：推一条，于是后退键能回到没筛选的那一列表
  // - 筛选中继续敲字：替换，否则每敲一个字推一条，按一次后退只退一个字母
  //
  // 一开始两种情况都写成 replace: true，结果是第一次搜索把 /wiki 那条历史记录
  // 顶掉了 —— 按后退直接跳过整个列表页，回到更早的地方。浏览器里试出来的。
  const go = (next: { search: string; tag: string }) => {
    const params: Record<string, string> = {};
    if (next.search.trim()) params.search = next.search.trim();
    if (next.tag) params.tag = next.tag;

    router.get('/wiki', params, {
      preserveState: true,
      preserveScroll: true,
      replace: filtering,
    });
  };

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return undefined;
    }
    const timer = setTimeout(() => go({ search: draft, tag }), DEBOUNCE);
    return () => clearTimeout(timer);
  }, [draft, tag]);

  // 正在筛的那个标签一定要能被显示出来，哪怕它只挂一页、进不了候选 ——
  // 「取消筛选」的唯一入口就是再点一次那个选中的 chip（输入框右端那个 ✕ 只清
  // 搜索词，不碰 tag）。它不渲染，人就被卡在一个自己解不掉的筛选里。
  const listed = tags.some((item) => item.name === tag);
  const shown = showAll ? tags : tags.slice(0, TAG_PREVIEW);
  const visible = shown.filter((item) => item.name !== tag);

  return (
    <div className="mb-4">
      <label
        className="flex items-center gap-2 rounded-lg border border-todo-line bg-todo-card
                   px-[11px] py-2"
      >
        <span className="text-[13px] leading-none text-todo-muted">🔍</span>
        <input
          // 边栏的「搜索」那一行与 ⌘K 都把光标送进这里。常量在 shell.ts ——
          // 三处必须是同一个字符串，而写错的表现是「点了没反应」，不报错。
          id={WIKI_SEARCH_INPUT_ID}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="搜标题与正文"
          className="flex-1 border-0 bg-transparent text-[14px] text-todo-ink outline-none
                     placeholder:text-todo-muted"
        />
        {/* 命中数放在框里的右端。它回答的是「我刚敲的这个词有没有用」，紧贴输入框
            才能被立刻看到；放到结果列表上面就晚了一步。 */}
        {resultCount !== null && (
          <span className="shrink-0 text-[12px] text-todo-muted">{resultCount} 条</span>
        )}
        {draft && (
          <button
            type="button"
            onClick={() => setDraft('')}
            className="shrink-0 text-[12px] text-todo-muted hover:text-todo-ink"
            title="清除搜索词"
          >
            ✕
          </button>
        )}
      </label>

      {(tags.length > 0 || tag) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {/* 选中的那个排在最前并带一个 ✕：光靠底色变化看不出「再点一次会取消」。
              放最前是因为它是当前状态，而状态该在最容易看到的位置。 */}
          {tag && (
            <button
              type="button"
              onClick={() => go({ search: draft, tag: '' })}
              className="rounded-full border border-transparent bg-todo-accent2 px-2.5 py-0.5
                         text-[12px] text-todo-accent"
            >
              {tag}
              <span className="ml-1 opacity-70">✕</span>
              {/* 只挂一页、本来进不了候选的标签，在这里说明一下自己为什么在。 */}
              {!listed && <span className="ml-1 opacity-50">1</span>}
            </button>
          )}

          {visible.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={() => go({ search: draft, tag: item.name })}
              className="rounded-full border border-todo-line bg-todo-card2 px-2.5 py-0.5
                         text-[12px] text-todo-ink2 hover:border-todo-line2"
            >
              {item.name}
              <span className="ml-1.5 text-[11px] text-todo-muted">{item.uses}</span>
            </button>
          ))}

          {/* 「全部 N 个」用的是**全量**的数（含只挂一页的那些），所以它同时也是
              一句实话：候选只有这么几个，是因为其余的都只挂在一页上。 */}
          {!showAll && tags.length > TAG_PREVIEW && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="px-1.5 py-0.5 text-[12px] text-todo-muted hover:text-todo-ink2"
            >
              还有 {tags.length - TAG_PREVIEW} 个 ⌄
            </button>
          )}
          {showAll && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="px-1.5 py-0.5 text-[12px] text-todo-muted hover:text-todo-ink2"
            >
              收起 ⌃
            </button>
          )}
          {totalTags > tags.length && (
            <span
              className="px-1 text-[11.5px] text-todo-muted"
              title={`另有 ${totalTags - tags.length} 个标签只挂在一页上，用搜索框找它们`}
            >
              · 共 {totalTags} 个
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
